import { useMemo, useState } from "react";
import { Label } from "./label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./select";

type Period = "AM" | "PM";

interface TimePickerDemoProps {
	date: Date | undefined;
	setDate: (date: Date | undefined) => void;
	/** Interval in minutes between each time option (default: 15). */
	stepMinutes?: number;
	/** Minimum allowable date and time. Options before this date-time will be disabled. */
	minDate?: Date;
}

const getTimeForOption = (
	timeStr: string,
	p: Period,
	baseDate: Date,
): Date => {
	const [hStr, mStr] = timeStr.split(":");
	const hour = Number.parseInt(hStr ?? "0", 10);
	const minute = Number.parseInt(mStr ?? "0", 10);
	const hour24 =
		p === "AM"
			? hour === 12
				? 0
				: hour
			: hour === 12
				? 12
				: hour + 12;

	const d = new Date(baseDate);
	d.setHours(hour24, minute, 0, 0);
	return d;
};

export function TimePicker12Demo({
	date,
	setDate,
	stepMinutes = 15,
	minDate,
}: TimePickerDemoProps) {
	const [period, setPeriod] = useState<Period>("AM");

	const baseDate = useMemo(() => {
		if (date) {
			return new Date(date);
		}
		if (minDate) {
			return new Date(minDate);
		}
		return new Date();
	}, [date, minDate]);

	const currentPeriod = useMemo((): Period => {
		if (date) {
			return date.getHours() >= 12 ? "PM" : "AM";
		}
		if (minDate) {
			const amEnd = new Date(baseDate);
			amEnd.setHours(11, 59, 59, 999);
			if (amEnd.getTime() < minDate.getTime()) {
				return "PM";
			}
		}
		return period;
	}, [date, minDate, baseDate, period]);

	const timeOptions = useMemo(() => {
		const step = Math.max(1, stepMinutes);
		const opts: string[] = [];

		for (let offset = 0; offset < 720; offset += step) {
			const hour24 = Math.floor(offset / 60);
			const minute = offset % 60;
			const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
			opts.push(`${hour12}:${minute.toString().padStart(2, "0")}`);
		}
		return opts;
	}, [stepMinutes]);

	const getCurrentTimeValue = () => {
		if (!date) {
			return "";
		}
		const step = Math.max(1, stepMinutes);
		const hour = date.getHours();
		const minutes = date.getMinutes();
		const totalMins = (hour % 12) * 60 + minutes;
		const snapped = Math.floor(totalMins / step) * step;
		const hour12 = Math.floor(snapped / 60) % 12 || 12;
		const min = snapped % 60;
		return `${hour12}:${min.toString().padStart(2, "0")}`;
	};

	const isTimeDisabled = (timeStr: string): boolean => {
		if (!minDate) {
			return false;
		}
		const candidate = getTimeForOption(timeStr, currentPeriod, baseDate);
		return candidate.getTime() < minDate.getTime();
	};

	const isPeriodDisabled = (p: Period): boolean => {
		if (!minDate) {
			return false;
		}
		const endOfPeriod = new Date(baseDate);
		if (p === "AM") {
			endOfPeriod.setHours(11, 59, 59, 999);
		} else {
			endOfPeriod.setHours(23, 59, 59, 999);
		}
		return endOfPeriod.getTime() < minDate.getTime();
	};

	const updateDate = (selectedTime: string, selectedPeriod: Period) => {
		const [hStr, mStr] = selectedTime.split(":");
		if (hStr === undefined || mStr === undefined) {
			return;
		}
		const hour = Number.parseInt(hStr, 10);
		const minute = Number.parseInt(mStr, 10);
		const hour24 =
			selectedPeriod === "AM"
				? hour === 12
					? 0
					: hour
				: hour === 12
					? 12
					: hour + 12;

		const newDate = new Date(baseDate);
		newDate.setHours(hour24, minute, 0, 0);

		if (minDate && newDate.getTime() < minDate.getTime()) {
			return;
		}

		setDate(newDate);
	};

	const handleTimeChange = (value: string) => {
		updateDate(value, currentPeriod);
	};

	const handlePeriodChange = (value: Period) => {
		setPeriod(value);
		const timeVal = getCurrentTimeValue();
		if (timeVal) {
			updateDate(timeVal, value);
		}
	};

	return (
		<div className="flex items-end gap-2">
			<div className="grid gap-1 text-left">
				<Label className="text-xs">Time</Label>
				<Select onValueChange={handleTimeChange} value={getCurrentTimeValue()}>
					<SelectTrigger className="w-24">
						<SelectValue placeholder="Select time" />
					</SelectTrigger>
					<SelectContent>
						{timeOptions.map((time) => (
							<SelectItem
								disabled={isTimeDisabled(time)}
								key={time}
								value={time}
							>
								{time}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="grid gap-1 text-left">
				<Label className="text-xs">Period</Label>
				<Select onValueChange={handlePeriodChange} value={currentPeriod}>
					<SelectTrigger className="w-16">
						<SelectValue placeholder="Select period" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem disabled={isPeriodDisabled("AM")} value="AM">
							AM
						</SelectItem>
						<SelectItem disabled={isPeriodDisabled("PM")} value="PM">
							PM
						</SelectItem>
					</SelectContent>
				</Select>
			</div>
		</div>
	);
}
